import { lazy, Suspense, type ReactNode } from "react";
import { Layout } from "@/components/Layout";
import { type Country, computeCityPrices, formatMoney, currencyFor, getCities } from "@/lib/gold-data";
import { historyQueryOptions } from "@/components/GoldChart";
import { buildCityFaqs, faqPageSchema, financialProductSchema, liveTimestamp, SITE_URL } from "@/lib/seo";
import { FaqSection } from "@/components/FaqSection";
import { WhatsAppShareInline } from "@/components/WhatsAppShareFab";
import { Link } from "@tanstack/react-router";
import { CityLink } from "@/components/CityLink";
import { ZakatCtaBanner } from "@/components/ZakatCtaBanner";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";
import { PremiumGoldCards } from "@/components/PremiumPriceCards";
import { MacroCards } from "@/components/MacroCards";
import { DisplayAd } from "@/components/DisplayAd";
import { todayLocal, todayLocalLong, tzForCountry } from "@/lib/today-local";
import { AnswerBox } from "@/components/seo/AnswerBox";
import { RelatedQueries } from "@/components/seo/RelatedQueries";
import { goldAnchorVariants, silverAnchorVariants } from "@/lib/seo/anchor-rotator";
import { TodayDateBlock } from "@/components/TodayDateBlock";
import { JumpLinks } from "@/components/JumpLinks";
import { getDateParts } from "@/lib/dateIntent";
import { PriceAlertBell } from "@/components/PriceAlertBell";
import { HubNextSteps } from "@/components/HubNextSteps";
import { RelatedSearches } from "@/components/RelatedSearches";
import { COUNTRY_KW, COUNTRY_CLUSTER_FAQS } from "@/lib/keyword-clusters";
import { OverseasMatrix, OverseasIntent } from "@/components/OverseasExpat";
import { DiasporaRateCard, DiasporaDeepContent, DiasporaLinkMesh } from "@/components/DiasporaGeoLayer";

const GoldChart = lazy(() =>
  import("@/components/GoldChart").then((m) => ({ default: m.GoldChart })),
);

export function CountryPage({ country, title, intro, extras, afterRate, crawlSlot }: { country: Country; title: string; intro: string; extras?: ReactNode; afterRate?: ReactNode; crawlSlot?: ReactNode }) {
  const tz = tzForCountry(country);
  const dateShort = todayLocal(tz);
  const dateLong = todayLocalLong(tz);
  const cName = country === "pakistan" ? "Pakistan" : "India";
  const otherCountry: Country = country === "pakistan" ? "india" : "pakistan";
  const otherName = otherCountry === "pakistan" ? "Pakistan" : "India";
  const sourcePath = `/${country}`;
  const answer =
    country === "pakistan"
      ? `The live 24K gold rate in Pakistan is recalculated every 10 minutes from the international spot price and the live USD/PKR rate. Today's price covers per tola, per 10 grams and per gram for 24K, 22K and 21K across all major cities including Karachi, Lahore and Islamabad. Scroll down for the full city-wise breakdown, a 30-day price chart and a free Zakat calculator.`
      : `The live 24K gold rate in India is updated every 10 minutes using the international spot price and the live USD/INR exchange rate. Today's rate covers per 10g, per gram and per tola for 24K, 22K and 21K across Mumbai, Delhi, Chennai and other major cities. Scroll down for the city-wise table, a 30-day chart and a daily zakat calculator.`;
  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pt-10 pb-6">
        <h1 className="text-3xl md:text-4xl font-bold">{title}</h1>
        <p className="mt-2 text-sm font-semibold text-primary" data-nosnippet>
          {dateLong} · live {cName} gold &amp; silver rate
        </p>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          <span className="font-medium text-foreground" data-nosnippet>As of {dateShort},</span>{" "}
          {intro}
        </p>
        <AnswerBox answer={answer} />
      </section>
      {/* 1. Chart (visual anchor) */}
      <Suspense fallback={<div className="mx-auto max-w-6xl px-4 py-10 min-h-[420px]" aria-busy="true">Loading chart…</div>}>
        <GoldChart country={country} />
      </Suspense>
      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd placement="mid-billboard" className="my-8" />
      </div>
      {/* 2. Live gold rate headline */}
      <Suspense fallback={<div className="mx-auto max-w-6xl px-4 pb-4 h-32" />}>
        <HeadlineRate country={country} />
      </Suspense>
      <section className="mx-auto max-w-6xl px-4 -mt-2 mb-4">
        <div className="flex flex-wrap items-center gap-2 min-h-[48px]">
          <PriceAlertBell country={country} metal="gold" currency={country === "pakistan" ? "PKR" : "INR"} />
          <WhatsAppShareInline />
        </div>
      </section>
      {/* Urdu / Hindi native-script CTA buttons (city / silver / zakat) */}
      <HubNextSteps variant={country === "pakistan" ? "gold-pk" : "gold-in"} />
      {/* 2b. Overseas Expat Live Conversion Matrix — diaspora currency ladder */}
      <OverseasMatrix country={country} />
      {/* 2c. Diaspora Multi-Currency Live Card (8 currencies + schema) */}
      <DiasporaRateCard country={country} />
      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd placement="inline" className="my-8" />
      </div>
      {/* 3. Urdu / Hindi native-script CTA block (per-country) */}
      {afterRate}
      {/* 4. Carat cards (24K / 22K / 21K / 18K) */}
      <div className="overflow-visible">
        <Suspense fallback={<div className="min-h-[600px]" />}>
          <FullBreakdown country={country} />
        </Suspense>
      </div>
      <SilverCrosslink country={country} />
      <div className="cv-auto">
      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd placement="inline-2" className="my-8" />
      </div>
      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd sticky placement="mid-billboard-2" className="my-8" />
      </div>
      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd placement="end-halfpage" className="my-8" />
      </div>
      <section className="mx-auto max-w-6xl px-4 pb-10">
        <div className="flex flex-wrap items-center gap-3 min-h-[48px]">
          <WhatsAppShareInline />
          <PriceAlertBell country={country} metal="gold" currency={country === "pakistan" ? "PKR" : "INR"} />
        </div>
      </section>
      <RelatedQueries
        sourcePath={sourcePath}
        title="Related live rates & tools"
        links={[
          { to: `/silver-rate-${country}`, variants: silverAnchorVariants(cName) },
          { to: `/${otherCountry}`, variants: goldAnchorVariants(otherName) },
          { to: `/silver-rate-${otherCountry}`, variants: silverAnchorVariants(otherName) },
          { to: `/uae`, variants: goldAnchorVariants("UAE") },
          { to: `/ar/gold-rate-saudi-arabia`, variants: goldAnchorVariants("Saudi Arabia") },
          { to: `/embed-tools`, variants: [
            `embed live gold rate widget on your site`,
            `free gold price embed widgets`,
            `add DrHint gold rate to your blog`,
            `embeddable gold price dashboard`,
            `gold rate iframe & code snippets`,
          ] },
          { to: `/tools`, variants: [
            `free gold rate calculator tools`,
            `gold price converter & zakat calculator`,
            `daily gold tools — tola, gram, ounce`,
            `gold rate tools for jewellers`,
            `live gold tools suite`,
          ] },
        ]}
      />
      {crawlSlot}
      {extras}
      <div className="mx-auto max-w-6xl px-4">
        <DisplayAd placement="related-multiplex" className="my-8" />
      </div>
      <CityLinksMesh country={country} />
      <RelatedSearches
        title={`People also search — ${cName} gold rate`}
        links={COUNTRY_KW[country]}
      />
      <OverseasIntent country={country} />
      {/* Diaspora deep content + link mesh */}
      <DiasporaDeepContent country={country} />
      <DiasporaLinkMesh country={country} />
      <GoldFooterFacts country={country} />
      </div>
    </Layout>
  );
}

// Compact link mesh — every city page linked from the hub for internal
// crawl equity. Points to dynamic /city/{country}/{slug} which exists
// for every entry in PAKISTAN_CITIES / INDIA_CITIES.
function CityLinksMesh({ country }: { country: Country }) {
  const cities = getCities(country);
  const cName = country === "pakistan" ? "Pakistan" : "India";
  return (
    <section className="mx-auto max-w-6xl px-4 py-8" aria-label={`All ${cName} city gold pages`}>
      <h2 className="text-xl md:text-2xl font-bold mb-2">
        {cName} — city-wise gold rate pages
      </h2>
      <p className="mb-4 text-sm text-muted-foreground">
        Every major {cName} city has its own live 24K / 22K / 21K gold page — chart, per-tola & per-gram rate, carat cards and city-specific FAQs.
      </p>
      <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
        {cities.map((c) => (
          <CityLink
            key={c.slug}
            country={country}
            city={c.slug}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:border-primary hover:bg-primary/5 transition-colors"
          >
            Gold rate {c.name} →
          </CityLink>
        ))}
      </div>
    </section>
  );
}

function HeadlineRate({ country }: { country: Country }) {
  const { data } = useGoldRates();
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const prices = computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: fx,
    country,
    cityPremium: 1.0,
  });
  const currency = currencyFor(country);
  const tz = country === "pakistan" ? "PKT" : "IST";
  const updated = liveTimestamp(data.fetchedAt, tz);
  const isPK = country === "pakistan";
  const headlineLabel = isPK ? "Per Tola (11.66 g)" : "Per 10 Grams";
  const headlinePrice = isPK ? prices.perTola24 : prices.per10g24;
  const secondaryLabel = isPK ? "Per 10 Grams" : "Per Tola (11.66 g)";
  const secondaryPrice = isPK ? prices.per10g24 : prices.perTola24;
  return (
    <section className="mx-auto max-w-6xl px-4 pb-2">
      <div className="relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-card via-card to-gold/5 p-6 md:p-8 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
              <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              Live · 24K Gold · {isPK ? "Pakistan" : "India"}
            </div>
            <div className="mt-2 text-sm text-muted-foreground">Today 24K Gold Rate — {headlineLabel}</div>
            <div className="mt-2 text-4xl md:text-6xl font-extrabold tracking-tight text-gold leading-none">
              {formatMoney(headlinePrice, currency)}
            </div>
            <div className="mt-3 text-sm text-foreground/80">
              <span className="font-semibold">{secondaryLabel}:</span>{" "}
              <span className="font-bold">{formatMoney(secondaryPrice, currency)}</span>
              <span className="mx-2 text-muted-foreground">·</span>
              <span className="font-semibold">Per Gram:</span>{" "}
              <span className="font-bold">{formatMoney(prices.perGram24, currency)}</span>
            </div>
            <div className="mt-2 text-xs text-muted-foreground" data-nosnippet>
              Updated <time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FullBreakdown({ country }: { country: Country }) {
  const { data } = useGoldRates();
  const currency = currencyFor(country);
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const prices = computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: fx,
    country,
    cityPremium: 1.0,
  });
  const tz = country === "pakistan" ? "PKT" : "IST";
  const updated = liveTimestamp(data.fetchedAt, tz);

  const countryName = country === "pakistan" ? "Pakistan" : "India";
  const mainUnit = country === "pakistan" ? "tola" : "10g";
  const mainPrice = country === "pakistan" ? prices.perTola24 : prices.per10g24;
  const productSchema = financialProductSchema({
    name: `24K Gold Rate ${countryName} (per ${mainUnit})`,
    description: `Live 24K, 22K and 21K gold rate in ${countryName} — per gram, per tola and per 10 grams. Updated every 10 minutes.`,
    price: mainPrice,
    currency,
    url: `${SITE_URL}/${country}`,
  });
  const faqs = buildCityFaqs(country, countryName);
  const faqSchema = faqPageSchema(faqs);

  return (
    <section className="mx-auto max-w-6xl px-4 pb-6">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <p className="mb-3 text-xs font-medium text-primary" data-nosnippet>
        <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse mr-2 align-middle" />
        Live Updated: <time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time>
      </p>
      <h2 className="text-xl md:text-2xl font-bold mb-4">Full national rate breakdown</h2>
      <PremiumGoldCards prices={prices} currency={currency} regionLabel={countryName} />

      <MacroCards country={country} />

    </section>
  );
}

function SilverCrosslink({ country }: { country: Country }) {
  const href = country === "pakistan" ? "/silver-rate-pakistan" : "/silver-rate-india";
  const label = country === "pakistan" ? "Pakistan" : "India";
  return (
    <section className="mx-auto max-w-6xl px-4 pb-2">
      <Link
        to={href}
        className="group block rounded-2xl border border-border bg-gradient-to-br from-card via-card to-muted/30 p-5 shadow-sm hover:shadow-md transition-shadow min-h-[112px]"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary">
              Silver Price Today · {label}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              Live chandi rate · per 1 tola & per 10 gram · refreshed every 10 minutes
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow group-hover:bg-primary/90 transition-colors">
            View Silver →
          </span>
        </div>
      </Link>
    </section>
  );
}

export { goldRatesQueryOptions as ratesQueryOptions, historyQueryOptions };

function TodayDateBlockWired({ country }: { country: Country }) {
  const { data } = useGoldRates();
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const prices = computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: fx,
    country,
    cityPremium: 1.0,
  });
  const currency = currencyFor(country);
  const date = getDateParts(country);
  const isPK = country === "pakistan";
  const fmt = (n: number) => formatMoney(isPK ? Math.round(n) : Math.round(n), currency);
  return (
    <TodayDateBlock
      country={country}
      date={date}
      price24Label={fmt(isPK ? prices.perTola24 : prices.per10g24)}
      price22Label={fmt(isPK ? prices.perTola22 : prices.per10g22)}
      price21Label={fmt(isPK ? prices.perTola21 : prices.per10g21)}
      unitLabel={isPK ? "per tola" : "per 10 gram"}
    />
  );
}

function PostCityDateLinks({ country }: { country: Country }) {
  return (
    <>
      <TodayDateBlockWired country={country} />
      <JumpLinks country={country} date={getDateParts(country)} />
      <section className="mx-auto max-w-6xl px-4 pb-8">
        <div className="min-h-[48px]"><ZakatCtaBanner /></div>
      </section>
    </>
  );
}

function GoldFooterFacts({ country }: { country: Country }) {
  const countryName = country === "pakistan" ? "Pakistan" : "India";
  const faqs = buildCityFaqs(country, countryName);

  return (
    <>
      <PostCityDateLinks country={country} />
      <FaqSection faqs={faqs} title={`${countryName} gold rate — FAQs`} />
    </>
  );
}
